/**
 * Which home page the site shows, and the owner's switcher for it.
 *
 * The live choice is one row on the backend (`/api/v1/site-settings/`): every visitor's home page
 * reads it, and only an admin account can change it. An admin can also PREVIEW any layout in their
 * own browser without changing what anyone else sees; that preview lives in localStorage.
 *
 * The last answer is cached in localStorage too, so a returning visitor gets their home page at
 * once instead of a blank screen while the request is in flight.
 */
import { hostApi } from "../../../constants";
import apiRequest from "../../../../utils/apiClient";

export type HomeLayout = "flagship" | "simple" | "w" | "y" | "z";
export type HomeSkin = "cream" | "red" | "dark";
export type HomeHeadline = "becomes" | "is" | "cut" | "cherries";

export interface SiteSettings {
  home_layout: HomeLayout;
  home_skin: HomeSkin;
  home_headline: HomeHeadline;
  can_edit: boolean;
}

export const HOME_LAYOUTS: HomeLayout[] = ["flagship", "simple", "w", "y", "z"];
export const HOME_SKINS: HomeSkin[] = ["cream", "red", "dark"];
export const HOME_HEADLINES: HomeHeadline[] = ["becomes", "is", "cut", "cherries"];

/** What the site shows if the backend can't be reached: today's live home page. */
export const FALLBACK: SiteSettings = { home_layout: "flagship", home_skin: "cream", home_headline: "becomes", can_edit: false };

const ENDPOINT = `${hostApi}site-settings/`;
const CACHE_KEY = "cc_site_settings";
const PREVIEW_KEY = "cc_site_preview";

const isLayout = (v: unknown): v is HomeLayout => HOME_LAYOUTS.includes(v as HomeLayout);
const isSkin = (v: unknown): v is HomeSkin => HOME_SKINS.includes(v as HomeSkin);
export const isHeadline = (v: unknown): v is HomeHeadline => HOME_HEADLINES.includes(v as HomeHeadline);

const clean = (raw: any): SiteSettings => ({
  home_layout: isLayout(raw?.home_layout) ? raw.home_layout : FALLBACK.home_layout,
  home_skin: isSkin(raw?.home_skin) ? raw.home_skin : FALLBACK.home_skin,
  home_headline: isHeadline(raw?.home_headline) ? raw.home_headline : FALLBACK.home_headline,
  can_edit: raw?.can_edit === true,
});

export const readCachedSettings = (): SiteSettings | null => {
  try {
    const s = localStorage.getItem(CACHE_KEY);
    return s ? clean(JSON.parse(s)) : null;
  } catch (e) {
    return null;
  }
};

const writeCache = (s: SiteSettings) => {
  try {
    // Never cache "may edit": a browser someone else signs into must not show them the switcher.
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...s, can_edit: false }));
  } catch (e) {
    /* ignore */
  }
};

export const fetchSiteSettings = async (): Promise<SiteSettings> => {
  const res = await apiRequest(ENDPOINT, { method: "GET" });
  if (!res.ok) throw new Error(`site-settings ${res.status}`);
  const s = clean(await res.json());
  writeCache(s);
  return s;
};

export const saveSiteSettings = async (p: Preview): Promise<SiteSettings> => {
  const res = await apiRequest(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ home_layout: p.layout, home_skin: p.skin, home_headline: p.headline }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Couldn't save (${res.status})`);
  const s = clean(body);
  writeCache(s);
  return s;
};

/** The admin's own preview: what only this browser sees. Null = show the live one. */
export interface Preview {
  layout: HomeLayout;
  skin: HomeSkin;
  headline: HomeHeadline;
}

export const readPreview = (): Preview | null => {
  try {
    const p = JSON.parse(localStorage.getItem(PREVIEW_KEY) || "null");
    return p && isLayout(p.layout) && isSkin(p.skin)
      ? { layout: p.layout, skin: p.skin, headline: isHeadline(p.headline) ? p.headline : FALLBACK.home_headline }
      : null;
  } catch (e) {
    return null;
  }
};

export const writePreview = (p: Preview | null) => {
  try {
    if (p) localStorage.setItem(PREVIEW_KEY, JSON.stringify(p));
    else localStorage.removeItem(PREVIEW_KEY);
  } catch (e) {
    /* ignore */
  }
};
